import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, Chip, Collapse, Paper, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import store from '../../utilities/store';
import { StateManager } from '../../utilities/stateManager';

// Work items from mechanics' clock-out notes, waiting for a manager. A "match"
// adds the note to an existing line. A "new" item becomes a 0-hour line. The
// backend never writes a service line until a manager accepts here, so notes
// cannot duplicate lines on their own.
export default function ServiceSuggestionsPanel({ orderId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [names, setNames] = useState({});
  // Ids resolved locally but not yet confirmed. They stay hidden while the
  // request runs, and come back if the request fails.
  const [hidden, setHidden] = useState({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!orderId) return undefined;
    return store.subscribeServiceSuggestions(orderId, setSuggestions);
  }, [orderId]);

  const visible = suggestions.filter(s => !hidden[s.id]);
  if (visible.length === 0) return null;

  const resolve = async (suggestion, action) => {
    setHidden(prev => ({ ...prev, [suggestion.id]: true }));
    try {
      const name = names[suggestion.id] ?? suggestion.name;
      const res = await store.resolveServiceSuggestion(suggestion.id, action, name);
      if (!res || !res.success) throw new Error(res?.error || 'Could not save');
    } catch (e) {
      setHidden(prev => { const next = { ...prev }; delete next[suggestion.id]; return next; });
      StateManager.setAlertAndOpen(e?.message || 'Could not save', 'error');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <ButtonBase
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        sx={{ width: '100%', justifyContent: 'space-between', borderRadius: 1 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Mechanic notes to review ({visible.length})
        </Typography>
        <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </ButtonBase>
      <Collapse in={open}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1.5 }}>
          Accept to add each note to the service lines. Dismiss anything that is already covered.
        </Typography>
        {visible.map(s => {
          return (
            <Box key={s.id} sx={{ py: 1.25, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                  <Chip
                    size="small"
                    label={s.type === 'match' ? 'Matches a line' : 'New line'}
                    color={s.type === 'match' ? 'success' : 'info'}
                    variant="outlined"
                  />
                  {s.mechanicName && <Typography variant="caption" color="text.secondary">{s.mechanicName}</Typography>}
                </Box>
                {s.type === 'match' ? (
                  <Typography variant="body2"><strong>{s.serviceName}</strong></Typography>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Line name"
                    value={names[s.id] ?? s.name}
                    onChange={(e) => setNames(prev => ({ ...prev, [s.id]: e.target.value }))}
                    sx={{ my: 0.5 }}
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  Note: {(s.notes || []).join('; ')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={() => resolve(s, 'dismiss')}>Dismiss</Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => resolve(s, 'accept')}
                  disabled={s.type === 'new' && !String(names[s.id] ?? s.name).trim()}
                >
                  {s.type === 'match' ? 'Add note' : 'Add line'}
                </Button>
              </Box>
            </Box>
          );
        })}
      </Collapse>
    </Paper>
  );
}
